import { categories, findCategoryBySubCategory, gamification as gamificationMapper } from '@/@constants';
import {
  CategoryEnum,
  CommentType,
  ConversationType,
  DaysEnum,
  ExtraProductByTypeMap,
  MediaType,
  MessageType,
  ProductType,
  RecommendationType,
  SocialLinkType,
  UserType,
} from '@/@types';
import { mapProviders } from './constants';
import { CDNImage, isProductOfType } from './utils';

export const comment = (commentObj: any) => {
  return {
    id: commentObj.id,
    message: commentObj.message,
    date: new Date(commentObj.createAt),
    liked: commentObj.liked,
    likes: commentObj.likes,
    user: {
      id: commentObj.userSourceId,
      avatar: CDNImage(commentObj.images?.[0]?.original),
    },
  } as CommentType;
};

const weekdaysTrans = {
  lunes: 'monday',
  martes: 'tuesday',
  miércoles: 'wednesday',
  jueves: 'thursday',
  viernes: 'friday',
  sábado: 'saturday',
  domingo: 'sunday',
} as {
  [x: string]: DaysEnum;
};

const generetaExtraByType = <T extends ProductType>(product: T, extraInfo: any = {}): ProductType => {
  if (isProductOfType(product, ['restaurants'])) {
    const extra: ExtraProductByTypeMap['restaurants'] = {
      bookingProviders: extraInfo.bookingProviders,
      availability: [],
      zones: [],
      location: {
        geo: extraInfo.latitude
          ? {
              lat: Number(extraInfo.latitude),
              lng: Number(extraInfo.longitude),
            }
          : undefined,
        fullAddress: extraInfo.address || product.address,
      },
      phone: extraInfo.phone || product.phone,
      perks: {
        hasDelivery: extraInfo.hasDelivery,
        paymentMethods: extraInfo.paymentMethods,
      },
      price: extraInfo.price,
      workingHours: extraInfo.workingHours
        ? Object.entries(weekdaysTrans).reduce((acc, [key, weekDay]) => {
            const info = extraInfo.workingHours[key];

            if (!info) {
              return acc;
            }

            return acc.concat([
              {
                weekDay,
                times:
                  info
                    .map((str) => {
                      const [from, to] = str.split('–').map((x) => {
                        return x.trim();
                      });
                      return to ? { from, to } : false;
                    })
                    .filter(Boolean) || [],
              },
            ]);
          }, [] as any[])
        : [],
    };
    return {
      ...product,
      ratings: product.ratings
        .concat([
          {
            source: 'michelin',
            value: extraInfo.michelinStar || (extraInfo.michelin ? 0 : -1),
          },
          {
            source: 'google',
            value: extraInfo.rating,
            votes: extraInfo.reviewCount,
          },
        ])
        .filter(({ value }) => {
          return value !== undefined;
        }),
      extraInfo: extra,
    } as T;
  }
  return product;
};

export const product = (productObj: any, recommendationObj?: any): ProductType | undefined => {
  if (!productObj) {
    return undefined;
  }
  let preview: any = null;
  if (productObj.links?.youtube?.id) {
    preview = {
      type: 'youtube',
      url: productObj.links?.youtube.url,
      id: productObj.links?.youtube.id,
    };
  }
  if (productObj?.infoV2?.youtube) {
    preview = {
      type: 'youtube',
      url: `https://youtube.com/watch?v=${productObj?.infoV2?.youtube}`,
      id: productObj?.infoV2?.youtube,
    };
  }
  if (productObj?.youtube) {
    preview = {
      type: 'youtube',
      url: `https://youtube.com/watch?v=${productObj?.youtube}`,
      id: productObj?.youtube,
    };
  }
  if (productObj.links?.generic?.url.startsWith('https://p.scdn.co')) {
    preview = {
      type: 'music',
      url: productObj.links?.generic?.url,
      id: 'generic',
    };
  }

  const data = productObj.infoV2 || productObj;
  const releaseDate = new Date(data.releaseDate);
  let category = findCategoryBySubCategory(String(productObj.subcategoryId));
  if (category === categories.products && productObj.googleplacesId) {
    category = categories.restaurants;
  }
  const shouldUseRecoImage = ![categories.movies.name, categories.series.name, categories.music.name].includes(category.name);

  const baseProduct = {
    type: (category?.name || 'products') as CategoryEnum,
    id: String(productObj.productId || productObj.id),
    category,
    subcategories: [],
    oldSubcategory: String(productObj.subcategoryId),
    name: data.name || productObj.name,
    album: data.albumName,
    authors: (data.author ? [data.author] : data.artistsV2) || [],
    overview: data.overview,
    description: data.description || data.overview,
    image: CDNImage(recommendationObj?.images?.[0]?.original || data.image),
    releaseDate: releaseDate.getFullYear() !== 1970 ? releaseDate : undefined,
    address: productObj.address,
    url: data.url || productObj.url,
    media: (recommendationObj
      ? [
          {
            type: 'video',
            source: recommendationObj.video,
          },
          shouldUseRecoImage
            ? {
                type: 'image',
                source: CDNImage(recommendationObj.images?.[0]?.original),
              }
            : {},
        ]
      : []
    )
      .concat([
        {
          type: 'image',
          source: CDNImage(data.image),
        },
      ])
      .filter((x) => {
        return x.source;
      }) as MediaType[],
    preview,
    recommendations: {
      total: parseInt(productObj.totalRecommendations || productObj.recommendations || 0, 10),
      items: [] as RecommendationType[],
    },
    genres:
      data.genresV2 ||
      // for some unknown reason the genres could return this format { "genre1", "genre2" }
      // replace {} to [] to correctly arse it as an array
      (typeof data.genres === 'string' ? JSON.parse(data.genres.replace(/^\{/, '[').replace(/\}$/, ']')) : data.genres),
    // artists: typeof data.artists === 'string' ? JSON.parse(data.artists) : data.artists,
    runtime: data.runtime || (data.duration_ms ? data.duration_ms / 60000 : undefined),
    rating: data.chiles,
    isSaved: data.isSaved || productObj.isSaved,
    popularity: data.popularity,
    ratings: [
      {
        source: 'chiles',
        value: data.chiles,
        total: 5,
      },
    ],
    totalRecomendations: parseInt(productObj.totalRecommendations || productObj.recommendations || 0, 10),
    providers: Object.keys(mapProviders)
      .filter((item) => {
        return !!data[item] || productObj[item];
      })
      .map((item) => {
        return {
          ...mapProviders[item as string],
          url: data[item] || productObj[item],
        };
      }),

    createAt: new Date(data.createdAt),
    updateAt: new Date(data.updateAt),
    extra: {},
  } as unknown as ProductType;
  return generetaExtraByType(baseProduct, productObj.extraInfo);
};

export const user = (userObj: any) => {
  if (!userObj) {
    return undefined;
  }

  const links: SocialLinkType[] = [];

  if (userObj.youtube) {
    links.push({
      type: 'youtube',
      url: userObj.youtube.url,
      name: userObj.youtube.channel,
    });
  }
  if (userObj.instagram) {
    links.push({
      type: 'instagram',
      url: userObj.instagram,
      name: userObj.instagram.split('/').filter(Boolean).at(-1),
    });
  }
  if (userObj.tiktok) {
    links.push({
      type: 'tiktok',
      url: userObj.tiktok,
      name: userObj.tiktok.split('/').filter(Boolean).at(-1),
    });
  }

  return {
    id: userObj.id || userObj.userId,
    name: userObj.name || userObj.username,
    surname: userObj.surname || userObj.usersurname,
    lastname: userObj.lastname || userObj.usersurname,
    nickname: userObj.nickname,
    avatar: CDNImage(userObj.avatar?.fullUrl || userObj.image || userObj.owner_image),
    email: userObj.email,
    level: gamificationMapper[userObj.level],
    verified: userObj.verified,
    follows: { total: userObj.follows },
    followers: { total: userObj.followers },
    isMyFollower: userObj.isMyFollower,
    isFollowed: userObj.isFollowed,
    biography: userObj.biography,
    language: userObj.lang,
    country: userObj.country,
    recommendationCount: userObj.recommendations,
    links,
  } as UserType;
};

export const message = (messageObj: any, members: { [userId: string]: UserType }): MessageType => {
  return {
    id: messageObj.id,
    sender: members[messageObj.senderId],
    data: messageObj.data,
    createdAt: new Date(messageObj.createAt).toUTCString(),
    seenAt: messageObj.seenAt && new Date(messageObj.seenAt),
  };
};

export const conversation = (conversationObj: any, menssages?: any[]): ConversationType => {
  const members = conversationObj.members.map(user).reduce((acc, current) => {
    return {
      ...acc,
      [current.id]: current,
    };
  }, {});
  return {
    id: conversationObj.id,
    members,
    unread: conversationObj.unread || 0,
    messages:
      menssages?.map((msg) => {
        return message(msg, members);
      }) || [],
  };
};

export const feed = ({ products, collections, recommendations, users }: any) => {
  const userInfo = user(users[0]);
  const [collection] = collections || [];
  if (!products[0]) {
    return undefined;
  }
  const productInfo = product(products[0] || {}, recommendations[0]);
  const commentInfo = recommendations[0]
    ? {
        id: recommendations[0].id,
        message: recommendations[0].message,
        date: new Date(recommendations[0].createAt),
        liked: recommendations[0].liked,
        likes: recommendations[0].likes,
        user: {
          id: recommendations[0].userSourceId,
          avatar: CDNImage(recommendations[0].images?.[0]?.original),
        },
      }
    : undefined; // parsers.V2.comment(recommendations[0]);

  return {
    user: userInfo,
    comment: commentInfo,
    recommendation: commentInfo,
    collection: collection && {
      ...collection,
      owner: userInfo,
    },
    product: productInfo,
  };
};
